import { FileText, X } from 'lucide-react';
import { useId, useState, type ReactElement } from 'react';

import { TextField } from '@/components/ui/TextField';

import { useTemplateCandidates } from '../use-template-candidates';
import { errorAdvice } from '@/lib/error-advice';

// Selector de la plantilla de un tipo de documento (G3-4; 04-GC-P08 §6.A, 04-GC-A01 a
// A14): busca entre los documentos vigentes y solo deja elegir los que tienen su archivo
// en el LMS, porque de ese archivo nace cada documento nuevo del tipo.

export interface TemplateRef {
  id: string;
  code: string;
  title: string | null;
}

function describe(t: TemplateRef): string {
  return t.title === null ? t.code : `${t.code} · ${t.title}`;
}

export function TemplatePicker({
  value,
  onChange,
}: {
  value: TemplateRef | null;
  onChange: (next: TemplateRef | null) => void;
}): ReactElement {
  const labelId = useId();
  const [term, setTerm] = useState('');
  const results = useTemplateCandidates(term);
  const searching = term.trim().length >= 2;

  return (
    <div className="fld" role="group" aria-labelledby={labelId}>
      <span id={labelId} className="fld-lab">
        Plantilla
      </span>
      {value === null ? (
        <p className="fld-note">
          Sin plantilla: el archivo de cada documento nuevo se vincula a mano.
        </p>
      ) : (
        <div className="value-chosen">
          <FileText size={16} aria-hidden="true" />
          <span>
            <strong className="code">{value.code}</strong>
            {value.title !== null && ` · ${value.title}`}
          </span>
          <button type="button" className="btn mut sm" onClick={() => onChange(null)}>
            <X size={14} aria-hidden="true" />
            Quitar plantilla
          </button>
        </div>
      )}
      <TextField
        label="Buscar plantilla"
        value={term}
        maxLength={120}
        placeholder="Código o nombre, por ejemplo 04-GC-A03"
        hint="Documentos vigentes. Solo se eligen los que tienen su archivo en el LMS."
        error={null}
        onChange={setTerm}
      />
      {searching && results.isFetching && <p className="soft">Buscando…</p>}
      {searching && results.isError && (
        <p className="fld-error" role="alert">
          No se pudo buscar. {errorAdvice(results.error)}
        </p>
      )}
      {searching && results.data !== undefined && results.data.length === 0 && (
        <p className="soft">Ningún documento vigente coincide con «{term.trim()}».</p>
      )}
      {searching && results.data !== undefined && results.data.length > 0 && (
        <ul className="pick-list" aria-label="Documentos vigentes">
          {results.data.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="btn mut sm"
                disabled={!d.lmsLinked}
                aria-pressed={value?.id === d.id}
                onClick={() => {
                  onChange({ id: d.id, code: d.code, title: d.title });
                  setTerm('');
                }}
              >
                {describe(d)}
                {!d.lmsLinked && ' (sin archivo en el LMS)'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
